import { useContext, useEffect, useState } from 'react';
import { EditItem, EditSection } from '../../components/Layout';
import { Button } from 'reactstrap';
import { FaLock } from 'react-icons/fa';
import { Environment, getEnvironment } from '@/utils/env';
import i18n from '@/utils/i18n';
import { Communicator } from '../../tools/Communication';
import { ContentSection } from '../../components/Layout/ContentSection';
import {
  IsVytalEmployee,
  ViewerContext,
} from '../../providers/ViewerContextProvider';

const environment = getEnvironment();

type WebsiteWidgetProps = {
  merchantStoreId: string | undefined | null;
  title: string;
};

export default function WebsiteWidgetTab({
  merchantStoreId,
  title,
}: WebsiteWidgetProps) {
  const viewer = useContext(ViewerContext);
  const [badges, setBadges] = useState<boolean>(true);
  const [widgetPublicKey, setWidgetPublicKey] = useState<string | null>(null);
  const [includeAllStoresFromMerchant, setIncludeAllStoresFromMerchant] =
    useState<boolean>(true);
  const [darkMode, setDarkMode] = useState<boolean>(false);
  const [language, setLanguage] = useState<
    'auto' | 'de' | 'en' | 'fr' | 'es' | 'se' | 'hu' | 'pt'
  >('auto');

  useEffect(() => {
    (async () => {
      if (merchantStoreId) {
        const response = await Communicator.Get(
          '/Widget/GetWidgetStoreSecrets',
          { merchantStoreId: encodeURIComponent(merchantStoreId) },
        );
        const widgetStoreSecrets = await response.json();
        setWidgetPublicKey(widgetStoreSecrets.SavedPackagesWidgetPublicKey);
      }
    })();
  }, [merchantStoreId]);

  if (!merchantStoreId) {
    return <p>{i18n.t('Common:loading')}</p>;
  }

  const widgetWidth = '100%';
  const widgetHeight = '190';

  let env = 'prod';
  let host = 'https://partner.vytal.org';
  if (environment === Environment.Development) {
    env = 'dev';
    host = 'https://partner-dev.vytal.org';
  } else if (environment === Environment.Local) {
    env = 'local';
    host = 'https://localhost:5001';
  }

  const widgetHtml =
    `<iframe src="${host}/Widget/SavedPackages?merchantStoreId=${encodeURIComponent(
      merchantStoreId,
    )}` +
    `&publicApiKey=${widgetPublicKey}&env=${env}&includeAllStores=${
      includeAllStoresFromMerchant ? 'true' : 'false'
    }` +
    `&version=3&darkMode=${darkMode}&badges=${badges}&language=${language}" allowtransparency="true" frameborder="0" scrolling="no" ` +
    `width="${widgetWidth}" height="${widgetHeight}"></iframe>`;

  return (
    <ContentSection title={title} collapsable={false} key="content">
      <EditSection title={i18n.t('Common:banner')}>
        <div className="container pe-0 ps-0">
          <div className="row">
            <div className="col-sm">
              <EditItem title={i18n.t('WebsiteWidget:badges')}>
                <Button
                  size="sm"
                  className={!badges ? 'opacity-2' : ''}
                  onClick={() => setBadges(true)}
                >
                  {i18n.t('Common:yes')}
                </Button>{' '}
                <Button
                  size="sm"
                  className={badges ? 'opacity-2' : ''}
                  onClick={() => setBadges(false)}
                >
                  {i18n.t('Common:no')}
                </Button>{' '}
              </EditItem>
            </div>
            <div className="col-sm">
              <EditItem
                title={i18n.t('WebsiteWidget:allStores')}
                helpText={
                  IsVytalEmployee(viewer) ? (
                    <>
                      <FaLock
                        className="me-1"
                        style={{ position: 'relative', top: '-0.1rem' }}
                      />
                      {i18n.t('WebsiteWidget:allStoresHelpText')}
                    </>
                  ) : undefined
                }
              >
                <Button
                  size="sm"
                  className={!includeAllStoresFromMerchant ? 'opacity-2' : ''}
                  onClick={() => setIncludeAllStoresFromMerchant(true)}
                >
                  {i18n.t('Common:yes')}
                </Button>{' '}
                <Button
                  size="sm"
                  className={includeAllStoresFromMerchant ? 'opacity-2' : ''}
                  onClick={() => setIncludeAllStoresFromMerchant(false)}
                >
                  {i18n.t('Common:no')}
                </Button>
              </EditItem>
            </div>
          </div>
        </div>
        <div className="container pe-0 ps-0">
          <div className="row">
            <div className="col-sm">
              <EditItem title={i18n.t('WebsiteWidget:darkMode')}>
                <Button
                  size="sm"
                  className={!darkMode ? 'opacity-2' : ''}
                  onClick={() => setDarkMode(true)}
                >
                  {i18n.t('Common:yes')}
                </Button>{' '}
                <Button
                  size="sm"
                  className={darkMode ? 'opacity-2' : ''}
                  onClick={() => setDarkMode(false)}
                >
                  {i18n.t('Common:no')}
                </Button>{' '}
              </EditItem>
            </div>
          </div>
          <div className="row">
            <div className="col-sm">
              <EditItem title={i18n.t('WebsiteWidget:language')}>
                <Button
                  size="sm"
                  className={language !== 'auto' ? 'opacity-2' : ''}
                  onClick={() => setLanguage('auto')}
                >
                  {i18n.t('Common:automatic')}
                </Button>{' '}
                <Button
                  size="sm"
                  className={language !== 'de' ? 'opacity-2' : ''}
                  onClick={() => setLanguage('de')}
                >
                  {i18n.t('Common:german')}
                </Button>{' '}
                <Button
                  size="sm"
                  className={language !== 'en' ? 'opacity-2' : ''}
                  onClick={() => setLanguage('en')}
                >
                  {i18n.t('Common:english')}
                </Button>{' '}
                <Button
                  size="sm"
                  className={language !== 'fr' ? 'opacity-2' : ''}
                  onClick={() => setLanguage('fr')}
                >
                  {i18n.t('Common:french')}
                </Button>{' '}
                <Button
                  size="sm"
                  className={language !== 'es' ? 'opacity-2' : ''}
                  onClick={() => setLanguage('es')}
                >
                  {i18n.t('Common:spanish')}
                </Button>{' '}
                <Button
                  size="sm"
                  className={language !== 'se' ? 'opacity-2' : ''}
                  onClick={() => setLanguage('se')}
                >
                  {i18n.t('Common:swedish')}
                </Button>{' '}
                <Button
                  size="sm"
                  className={language !== 'hu' ? 'opacity-2' : ''}
                  onClick={() => setLanguage('hu')}
                >
                  {i18n.t('Common:hungarian')}
                </Button>{' '}
                <Button
                  size="sm"
                  className={language !== 'pt' ? 'opacity-2' : ''}
                  onClick={() => setLanguage('pt')}
                >
                  {i18n.t('Common:portuguese')}
                </Button>{' '}
              </EditItem>
            </div>
          </div>
        </div>

        <EditItem title={i18n.t('WebsiteWidget:htmlCode')}>
          <textarea
            className="form-control"
            rows={3}
            spellCheck={false}
            autoComplete="off"
            value={widgetHtml}
          />
          <p className="mt-1">{i18n.t('WebsiteWidget:htmlInfo')}</p>
        </EditItem>

        <EditItem title={i18n.t('Common:preview')}>
          <div dangerouslySetInnerHTML={{ __html: widgetHtml }} />
        </EditItem>
      </EditSection>
    </ContentSection>
  );
}
